'use client'

import { formatPrice } from '@/core/lib/utils/format-price'
import { Button } from '@/core/ui/atoms/button'
import { Divider } from '@heroui/react'
import { Star } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { BASE_ATTRIBUTES } from '../../enums/base-attributes'
import { LookupNode } from '../../model/attributes-lookup'
import { ProductDetails, Variant } from '../../model/product.model'
import { buildValueLookup, mapVariants } from '../../utils/attributes'
import { useUpdateCart } from '../../../cart/hooks/use-update-cart'
import { filterObject } from '@/core/lib/utils/filter-object'
import { useAuth } from '@/core/store/auth'
import { useUpdateSearchParams } from '@/core/hooks/use-update-search-params'
import { QUERY_ROUTES } from '@/core/constants/routes'
import { useTranslations } from 'next-intl'
import { useSelectedVariant } from '../../store/use-selected-variant'

type Props = { product: ProductDetails }

const getVariant = (variants: Variant[], selected: Record<number, string>) => {
  return variants.find(variant =>
    Object.entries(selected).every(([attrId, attrValue]) =>
      variant.attributes.some(
        attr => attr.id == Number(attrId) && attr.value == attrValue,
      ),
    ),
  )
}
const getValidVariants = (
  variants: Variant[],
  selected: Record<number, string>,
  untilAttrId?: number,
) => {
  return variants.filter(variant =>
    Object.entries(selected).every(([attrId, value]) => {
      if (untilAttrId && Number(attrId) === untilAttrId) return true

      return variant.attributes.some(
        a => a.id === Number(attrId) && a.value === value,
      )
    }),
  )
}
const getAvailableValues = (
  attrId: number,
  variants: Variant[],
  selected: Record<number, string>,
) => {
  const validVariants = getValidVariants(variants, selected, attrId)

  return Array.from(
    new Set(
      validVariants
        .map(v => v.attributes.find(a => a.id === attrId)?.value)
        .filter(Boolean),
    ),
  )
}

export function Attributes({ product }: Props) {
  const t = useTranslations()
  const { isAuthenticated } = useAuth()
  const { onUpdateCart, isPending } = useUpdateCart()
  const { updateSearchParam } = useUpdateSearchParams()
  const { setVariant } = useSelectedVariant()
  const variants = product.variants
  const price = product?.attributes.find(
    attr => attr.id === BASE_ATTRIBUTES.price,
  )?.value
  const compareAtPrice = product?.attributes.find(
    attr => attr.id === BASE_ATTRIBUTES.compare_at_price,
  )?.value

  const mapped = useMemo(() => mapVariants(variants), [variants])
  const valueLookup = useMemo(() => buildValueLookup(variants), [variants])

  const root = Object.values(mapped)[0]
  const [selected, setSelected] = useState<Record<number, string>>({})
  const [finalCost, setFinalCost] = useState<number | null>(null)

  const handleAddToCart = () => {
    const selectedVariant = getVariant(variants, selected)

    onUpdateCart({
      items: [
        filterObject({
          product_id: product.id,
          quantity: 1,
          variant_id: selectedVariant?.id ?? null,
        }),
      ],
    })
  }

  const autoSelectPath = useCallback(
    (
      node: LookupNode,
      selections: Record<number, string>,
      preserve: Record<number, string> = {},
    ): Record<number, string> => {
      if (!node.children || Object.keys(node.children).length === 0) {
        if (node.cost) setFinalCost(node.cost)
        return selections
      }

      const availableOptions = getAvailableValues(node.id, variants, selected)
      if (!availableOptions?.length) return selections

      const chosen = preserve[node.id] ?? availableOptions[0]
      selections[node.id] = chosen

      const childNode = Object.values(node.children)[0]
      if (childNode) return autoSelectPath(childNode, selections, preserve)

      return selections
    },
    [valueLookup],
  )

  useEffect(() => {
    if (!root) return
    const defaultSelected = autoSelectPath(root, {}, {})
    setSelected(defaultSelected)

    const variant = getVariant(variants, defaultSelected)
    if (variant?.price) {
      setFinalCost(Number(variant.price))
    }
    setVariant(variant ?? null)
  }, [root])

  const handleSelect = (attrId: number, value: string) => {
    if (!root) return

    const newSelected: Record<number, string> = {}
    let stop = false
    for (const id of Object.keys(selected)) {
      if (stop) continue
      newSelected[Number(id)] = selected[Number(id)]
      if (Number(id) === attrId) stop = true
    }

    newSelected[attrId] = value

    const startNode = findNodeById(root, attrId)
    if (startNode) {
      const childNode = Object.values(startNode.children)[0]
      if (childNode) autoSelectPath(childNode, newSelected)
    }

    setSelected({ ...newSelected })
    const selectedVariant = getVariant(variants, newSelected)
    if (selectedVariant?.price) {
      setFinalCost(Number(selectedVariant.price))
    }
    setVariant(selectedVariant ?? null)
  }

  const findNodeById = (node: LookupNode, id: number): LookupNode | null => {
    if (node.id === id) return node
    for (const child of Object.values(node.children ?? {})) {
      const found = findNodeById(child, id)
      if (found) return found
    }
    return null
  }
  const getColorHex = (attrId: number, value: string, variants: Variant[]) => {
    const attr = variants
      .flatMap(v => v.attributes)
      .find(a => a.id === attrId && a.value === value)

    return attr?.extra_value
  }
  const renderLevel = (node: LookupNode): React.ReactNode => {
    const isColorAttribute = node.name.toLowerCase() === 'color'
    if (node.id === BASE_ATTRIBUTES.cost) return null
    const options = valueLookup[node.id] ?? []
    const availableOptions = getAvailableValues(node.id, variants, selected)
    const selectedValue = selected[node.id] ?? null
    const childNode = Object.values(node.children)[0] ?? null

    const leafChild = Object.values(node.children).find(
      c => c.id === BASE_ATTRIBUTES.cost,
    )
    if (leafChild?.cost) setFinalCost(leafChild.cost)

    return (
      <div key={node.id} className='mb-2 sm:mb-5'>
        <Divider className='mt-5 mb-3' />
        <p className='mb-2 text-base font-semibold sm:text-lg md:text-xl'>
          {node.name}
        </p>
        <div className='flex flex-wrap gap-2 sm:gap-3'>
          {options.map(value => {
            const isDisabled = !availableOptions.includes(value)
            const isSelected = selectedValue === value
            const hex = getColorHex(node.id, value, variants)
            if (isColorAttribute && hex) {
              return (
                <button
                  key={value}
                  disabled={isDisabled}
                  onClick={() => !isDisabled && handleSelect(node.id, value)}
                  className={`h-8 w-8 rounded-full border-2 transition ${
                    isSelected ? 'scale-110 border-black' : 'border-gray-300'
                  } ${isDisabled ? 'cursor-not-allowed opacity-30' : 'cursor-pointer'}`}
                  style={{ backgroundColor: hex }}
                  title={value}
                />
              )
            }
            return (
              <button
                key={value}
                disabled={isDisabled}
                onClick={() => !isDisabled && handleSelect(node.id, value)}
                className={`flex ${isDisabled ? `cursor-auto` : `cursor-pointer`} items-center gap-2 rounded-xs px-2 py-1 text-sm ring transition ${
                  isSelected
                    ? 'ring-mountain-bluebird text-mountain-bluebird ring-2'
                    : isDisabled
                      ? 'cursor-not-allowed line-through opacity-40 ring-gray-100'
                      : 'ring-dim-gray hover:ring-mountain-bluebird'
                } `}
              >
                {value}
              </button>
            )
          })}
        </div>
        {childNode && renderLevel(childNode)}
      </div>
    )
  }

  const openLogin = () => {
    updateSearchParam(
      { [QUERY_ROUTES.login.name]: QUERY_ROUTES.login.value },
      { clientOnly: true },
    )
  }
  const discountPercentage = useMemo(() => {
    const sellingPrice = finalCost ?? price

    if (!compareAtPrice || !sellingPrice) return null
    if (Number(compareAtPrice) <= Number(sellingPrice)) return null

    const discount =
      ((Number(compareAtPrice) - Number(sellingPrice)) /
        Number(compareAtPrice)) *
      100

    return discount.toFixed(1)
  }, [compareAtPrice, finalCost, price])

  return (
    <>
      <div className='mb-2 flex items-center gap-2'>
        {compareAtPrice && compareAtPrice !== price && (
          <span
            className='text-base text-gray-400 sm:text-xl'
            style={{ textDecoration: 'line-through' }}
          >
            {formatPrice(compareAtPrice, product?.currency_symbol)}
          </span>
        )}

        <span className='text-creamed-cron text-xl font-bold sm:text-2xl lg:text-[24px]'>
          {formatPrice(finalCost ?? price, product?.currency_symbol)}
        </span>
      </div>
      {discountPercentage && (
        <span className='mb-2 inline-block rounded-sm bg-red-700 px-2 py-1 text-sm font-semibold text-white'>
          -{discountPercentage}%
        </span>
      )}
      <div className='flex items-center gap-2'>
        <Star className='fill-creamed-cron text-creamed-cron text-xl' />
        <span className='text-mountain-bluebird text-sm font-medium sm:text-base'>
          {product.rating}
        </span>
        {product.best_seller == '1' && (
          <span className='text-saffron bg-[#F5B22F33] px-1 leading-[17px]'>
            {t('products.Best seller')}
          </span>
        )}
      </div>
      {root && renderLevel(root)}
      <Divider className='mt-5 mb-3' />
      <Button
        className='mt-1 h-11 w-full font-semibold sm:py-3'
        onClick={isAuthenticated ? handleAddToCart : openLogin}
        loading={isPending}
      >
        {t('products.AddCart')}
      </Button>
      <Divider className='my-5' />
    </>
  )
}
